import { supabase } from '@/integrations/supabase/client';
import { PostgrestFilterBuilder } from '@supabase/postgrest-js';

/**
 * 🛡️ Safe Supabase Fetch Utility - v1.8.50
 * Provides bulletproof querying with automatic error handling and fallbacks
 */
export async function safeFetch<T>(
  query: PostgrestFilterBuilder<any, any, any, T> | Promise<{ data: T[] | null; error: any }>
): Promise<T[]> {
  try {
    const result = await query;
    if (result.error) {
      // Only log in development to reduce console noise in production
      if (process.env.NODE_ENV === 'development') {
        console.warn("⚠️ Safe fetch failed:", result.error.message || result.error);
      }
      return [];
    }
    return Array.isArray(result.data) ? result.data : [];
  } catch (err: any) {
    if (process.env.NODE_ENV === 'development') {
      console.warn("⚠️ Safe fetch caught error:", err.message || err);
    }
    return [];
  }
}

/**
 * 🛡️ Safe Single Record Fetch
 */
export async function safeFetchSingle<T>(
  query: PostgrestFilterBuilder<any, any, any, T> | Promise<{ data: T | null; error: any }>
): Promise<T | null> {
  try {
    const result = await query;
    if (result.error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn("⚠️ Safe fetch single failed:", result.error.message || result.error);
      }
      return null;
    }
    return result.data;
  } catch (err: any) {
    if (process.env.NODE_ENV === 'development') {
      console.warn("⚠️ Safe fetch single caught error:", err.message || err);
    }
    return null;
  }
}

/**
 * 🛡️ Safe Count Fetch
 */
export async function safeFetchCount(
  query: Promise<{ count: number | null; error: any }>
): Promise<number> {
  try {
    const result = await query;
    if (result.error) {
      console.warn("⚠️ Safe fetch count failed:", result.error.message || result.error);
      return 0;
    }
    return result.count ?? 0;
  } catch (err: any) {
    console.warn("⚠️ Safe fetch count caught error:", err.message || err);
    return 0;
  }
}
